import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { B } from '../../constants';
import { borderRadius, shadows, transitions, animationDurations, breakpoints } from '../../designTokens';

const sizeMap = {
  xs: { maxWidth: 320 },
  sm: { maxWidth: 400 },
  md: { maxWidth: 500 },
  lg: { maxWidth: 600 },
  xl: { maxWidth: 800 },
  full: { maxWidth: '95vw' },
};

const radiusMap = {
  xs: borderRadius.xs,
  sm: borderRadius.sm,
  md: borderRadius.md,
  lg: borderRadius.lg,
  xl: borderRadius.xl,
  pill: borderRadius.pill,
};

const positionAlign = {
  center: { alignItems: 'center', padding: 24 },
  top: { alignItems: 'flex-start', padding: '48px 24px' },
  bottom: { alignItems: 'flex-end', padding: '24px 24px 48px' },
};

const closeBtnPositionMap = {
  'top-right': { top: 16, right: 16 },
  'top-left': { top: 16, left: 16 },
  'bottom-right': { bottom: 16, right: 16 },
  'bottom-left': { bottom: 16, left: 16 },
};

const scrollBehaviorStyles = {
  inside: { overflowY: 'auto', maxHeight: '90vh' },
  outside: { overflow: 'visible' },
  body: { overflow: 'visible' },
};

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusable(el) {
  if (!el) return [];
  return [...el.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
}

let modalAnimationsInjected = false;

function ensureAnimations() {
  if (typeof document === 'undefined' || modalAnimationsInjected) return;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes modal-fade { from { opacity: 0; } to { opacity: 1; } }
    @keyframes modal-slide { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes modal-scale { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
    @keyframes modal-backdrop { from { opacity: 0; } to { opacity: 1; } }
  `;
  document.head.appendChild(style);
  modalAnimationsInjected = true;
}

let modalCounter = 1000;

function CloseButton({ onClick, style: extraStyle, closeIcon }) {
  return (
    <button
      onClick={onClick}
      aria-label="Close dialog"
      style={{
        position: 'absolute',
        width: 32, height: 32, borderRadius: '50%',
        background: 'rgba(0,0,0,0.05)',
        border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 18, color: B.muted, zIndex: 1,
        transition: `all ${transitions.fast}`,
        ...extraStyle,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.1)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.05)'; }}
    >
      {closeIcon || '✕'}
    </button>
  );
}

function Backdrop({ style: extraStyle }) {
  return (
    <div
      style={{
        position: 'absolute', inset: 0,
        animation: 'modal-backdrop 0.2s ease',
        ...extraStyle,
      }}
    />
  );
}

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  ariaLabel,
  position = 'center',
  animation = 'slide',
  backdropBlur = true,
  backdropBlurAmount = 4,
  radius = 'xl',
  closeButtonPosition = 'top-right',
  header,
  footer,
  closeOnBackdropClick = true,
  transitionDuration = 'normal',
  usePortal = true,
  scrollBehavior = 'inside',
  closeIcon,
  backdropColor = 'rgba(0,0,0,0.5)',
  zIndex: zIndexProp,
  maxHeight,
  padding,
  onOpen,
  initialFocusRef,
  blockScroll = true,
  centerContent,
  responsiveSizes,
}) {
  const [animate, setAnimate] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const containerRef = useRef(null);
  const previousFocusRef = useRef(null);
  const [autoZIndex] = useState(() => ++modalCounter);

  const effectiveZIndex = zIndexProp || autoZIndex;
  const transitionValue = transitions[transitionDuration] || transitionDuration || transitions.normal;
  const radiusValue = radiusMap[radius] || radiusMap.xl;

  const [responsiveSize, setResponsiveSize] = useState(size);
  useEffect(() => {
    if (!responsiveSizes) { setResponsiveSize(size); return; }
    const check = () => {
      const w = window.innerWidth;
      const bp = Object.keys(breakpoints)
        .sort((a, b) => parseFloat(breakpoints[a]) - parseFloat(breakpoints[b]));
      let matched = size;
      for (const key of bp) {
        if (w >= parseFloat(breakpoints[key]) && responsiveSizes[key]) {
          matched = responsiveSizes[key];
        }
      }
      setResponsiveSize(matched);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [size, responsiveSizes]);

  const s = sizeMap[responsiveSize] || sizeMap.md;
  const posStyle = positionAlign[position] || positionAlign.center;
  const closeBtnStyle = closeBtnPositionMap[closeButtonPosition] || closeBtnPositionMap['top-right'];
  const scrollStyle = scrollBehaviorStyles[scrollBehavior] || scrollBehaviorStyles.inside;

  const animationName = animation === 'fade' ? 'modal-fade'
    : animation === 'slide' ? 'modal-slide'
    : animation === 'scale' ? 'modal-scale'
    : null;

  const animationDuration = animationDurations.slideUp || '0.3s';

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e) => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    ensureAnimations();
  }, []);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      onOpen?.();
      const frame = requestAnimationFrame(() => setAnimate(true));
      return () => {
        cancelAnimationFrame(frame);
        setAnimate(false);
      };
    } else {
      setAnimate(false);
    }
  }, [isOpen, onOpen]);

  useEffect(() => {
    if (isOpen && blockScroll) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      if (blockScroll) document.body.style.overflow = '';
    };
  }, [isOpen, blockScroll]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
        return;
      }
      if (e.key === 'Tab' && containerRef.current) {
        const focusable = getFocusable(containerRef.current);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !containerRef.current) return;
    if (initialFocusRef?.current) {
      initialFocusRef.current.focus();
    } else {
      const focusable = getFocusable(containerRef.current);
      if (focusable.length > 0) {
        focusable[0].focus();
      }
    }
  }, [isOpen, initialFocusRef]);

  useEffect(() => {
    return () => {
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  const handleBackdropClick = useCallback((e) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose?.();
    }
  }, [closeOnBackdropClick, onClose]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const contentPadding = padding || (header || title ? '20px 28px 28px' : '28px');
  const contentMaxHeight = maxHeight || '90vh';

  const modalContent = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel || (typeof title === 'string' ? title : 'Dialog')}
      onClick={handleBackdropClick}
      style={{
        position: 'fixed', inset: 0, zIndex: effectiveZIndex,
        display: 'flex',
        ...posStyle,
      }}
    >
      <Backdrop
        style={{
          background: backdropColor,
          ...(backdropBlur ? {
            backdropFilter: `blur(${backdropBlurAmount}px)`,
            WebkitBackdropFilter: `blur(${backdropBlurAmount}px)`,
          } : {}),
        }}
      />
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          background: centerContent ? 'transparent' : 'white',
          borderRadius: radiusValue,
          width: '100%',
          maxWidth: s.maxWidth,
          maxHeight: contentMaxHeight,
          boxShadow: centerContent ? 'none' : shadows.xl,
          animation: animate && animationName && !reducedMotion
            ? `${animationName} ${animationDuration} ${transitionValue}`
            : undefined,
          ...scrollStyle,
          display: centerContent ? 'flex' : undefined,
          alignItems: centerContent ? 'center' : undefined,
          justifyContent: centerContent ? 'center' : undefined,
          textAlign: centerContent ? 'center' : undefined,
        }}
      >
        {showCloseButton && (
          <CloseButton
            onClick={handleClose}
            style={closeBtnStyle}
            closeIcon={closeIcon}
          />
        )}
        {(header || title) && !centerContent && (
          <div
            style={{
              padding: '24px 28px 0',
              fontFamily: "'Playfair Display',serif",
              fontSize: 22, fontWeight: 700, color: B.night,
            }}
          >
            {header || title}
          </div>
        )}
        <div style={{ padding: contentPadding }}>
          {children}
        </div>
        {footer && !centerContent && (
          <div
            style={{
              padding: '0 28px 24px',
              borderTop: `1px solid #e5e0d8`,
              paddingTop: 20,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  if (!isOpen) return null;

  if (usePortal) {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
}
